import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdPlayArrow, MdRefresh } from 'react-icons/md';

// Bas de la page Waterfall : leads refusés par Batiweb ou jamais envoyés, encore sur la Bourse (avec
// l'offre qui les recevrait), et leads déjà attribués par le waterfall.

type Reason = 'unsold' | 'timeout' | 'not_sellable' | 'blocked' | 'not_sent';

interface Contact {
    firstname: string | null;
    lastname: string | null;
    email: string | null;
    zipcode: string | null;
}

interface PreviewLead {
    _id: string;
    createdAt: string;
    contact: Contact;
    industry: string | null;
    subIndustry: string | null;
    region: string | null;
    reason: Reason | null;
    offerId: string | null;
    offerName: string | null;
    // Sans offre : pourquoi chaque offre locked active le refuse.
    mismatches: { offerName: string; reason: string }[];
}

interface DispatchedLead {
    _id: string;
    contact: Contact;
    industry: string | null;
    subIndustry: string | null;
    offerName: string | null;
    buyerEmail: string | null;
    reason: Reason;
    dispatchedAt: string;
    dispatchedBy: string;
    buyerPrice: number;
    leadPrice: number;
    approvalStatus: string | null;
    paidAt: string | null;
}

export const REASON_LABELS: Record<Reason, string> = {
    unsold: 'Annulé par Batiweb',
    timeout: 'Sans réponse (5 j ouvrés)',
    not_sellable: 'Non vendable (Batiweb)',
    blocked: 'Bloqué, hors Batiweb',
    not_sent: 'Jamais envoyé à Batiweb',
};

const APPROVAL_LABELS: Record<string, string> = {
    pending: 'En validation (48 h)',
    conflict: 'Contesté',
    approved: 'Validé',
    rejected: 'Refusé par l’acheteur',
};

const TABS = [
    { key: 'preview', label: 'En attente (aperçu)' },
    { key: 'dispatched', label: 'Attribués' },
] as const;
type Tab = typeof TABS[number]['key'];

const formatDate = (value?: string) => (value ? new Date(value).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const formatEuro = (value: number) => `${value.toFixed(2).replace('.', ',')} €`;
const todayIso = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Paris' });
const formatDay = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR');
const fullName = (c: Contact) => [c.firstname, c.lastname].filter(Boolean).join(' ') || '—';

export default function WaterfallLeadsSection({ refreshKey, onRun }: { refreshKey: number; onRun: () => void }) {
    const [tab, setTab] = useState<Tab>('preview');
    const [preview, setPreview] = useState<PreviewLead[]>([]);
    const [dispatched, setDispatched] = useState<DispatchedLead[]>([]);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [error, setError] = useState('');
    const [banner, setBanner] = useState('');
    // Vide = fenêtre par défaut (14 derniers jours). Sinon : tous les leads refusés depuis cette date.
    const [since, setSince] = useState('');
    // Chaque changement de date relance l'aperçu : seule la réponse de la dernière requête compte.
    const requestId = useRef(0);

    const fetchLeads = useCallback(async () => {
        const id = ++requestId.current;
        try {
            setLoading(true);
            setError('');
            const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
            const [p, d] = await Promise.all([
                axios.get(`${CONFIG.apiUrl}/admin/waterfall/preview`, { headers, params: since ? { since } : {} }),
                axios.get(`${CONFIG.apiUrl}/admin/waterfall/leads`, { headers }),
            ]);
            if (id !== requestId.current) return;
            setPreview(p.data.leads);
            setDispatched(d.data.leads);
        } catch (e: any) {
            if (id !== requestId.current) return;
            setPreview([]);
            setError(e.response?.data?.message || 'Impossible de charger les leads du waterfall.');
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [since]);

    const changeSince = (value: string) => {
        setSince(value);
        setBanner('');
    };

    useEffect(() => {
        fetchLeads();
    }, [fetchLeads, refreshKey]);

    const handleRun = async () => {
        try {
            setRunning(true);
            setError('');
            if (since && !window.confirm(`Attribuer aux offres locked tous les leads refusés par Batiweb depuis le ${formatDay(since)} ?`)) return;
            const res = await axios.post(`${CONFIG.apiUrl}/admin/waterfall/run`, since ? { since } : {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            const s = res.data;
            setBanner(`${s.candidates} lead(s) refusé(s) par Batiweb : ${s.dispatched} attribué(s), ${s.noOffer} sans offre (restent sur la Bourse)${s.skipped ? `, ${s.skipped} passé(s)` : ''}${s.failed ? `, ${s.failed} erreur(s)` : ''}.`);
            fetchLeads();
            onRun();
        } catch (e: any) {
            setError(e.response?.data?.message || 'Erreur lors du lancement du waterfall.');
        } finally {
            setRunning(false);
        }
    };

    const matched = preview.filter((l) => l.offerId).length;

    return (
        <div>
            <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-gray-200 dark:border-navy-600">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${
                            tab === t.key ? 'border-brand-500 text-brand-500' : 'border-transparent text-gray-500 hover:text-navy-700 dark:hover:text-white'
                        }`}
                    >
                        {t.label}
                        <span className="ml-1 text-xs text-gray-400">({t.key === 'preview' ? preview.length : dispatched.length})</span>
                    </button>
                ))}
                <div className="mb-2 ml-auto flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400">
                        Depuis le
                        <input
                            type="date"
                            value={since}
                            max={todayIso()}
                            onChange={(e) => changeSince(e.target.value)}
                            className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-normal text-navy-700 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                        />
                    </label>
                    {since && (
                        <button onClick={() => changeSince('')} className="text-xs font-semibold text-gray-500 hover:text-navy-700 dark:hover:text-white">
                            14 derniers jours
                        </button>
                    )}
                    <button
                        onClick={fetchLeads}
                        disabled={loading}
                        className="flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-navy-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-navy-700 dark:text-white"
                    >
                        <MdRefresh />
                        Actualiser
                    </button>
                    <button
                        onClick={handleRun}
                        disabled={running || loading || matched === 0}
                        title={matched === 0 ? 'Aucun lead en attente ne correspond à une offre active' : undefined}
                        className="flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                    >
                        <MdPlayArrow />
                        {running ? 'En cours...' : `Lancer le waterfall maintenant${matched ? ` (${matched})` : ''}`}
                    </button>
                </div>
            </div>

            {error && <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">{error}</div>}
            {banner && <div className="mb-4 rounded-lg bg-green-100 p-3 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">{banner}</div>}

            {tab === 'preview' && (
                <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                    {since ? (
                        <>
                            Leads no-match créés depuis le {formatDay(since)}, refusés par Batiweb ou jamais envoyés, encore sans acheteur. Le bouton attribue ceux qui
                            ont une offre ; les plus de 14 jours ne sont plus sur la Bourse et ne sont repris que par ce lancement manuel.
                        </>
                    ) : (
                        <>
                            Leads no-match de moins de 14 jours, refusés par Batiweb ou jamais envoyés, et encore sur la Bourse du Lead. Ceux qui ont une offre seront attribués
                            au prochain passage (chaque jour après la vérification Batiweb de 9h, ou avec le bouton). Les autres restent en vente sur la
                            Bourse. Choisissez une date pour reprendre des leads plus anciens.
                        </>
                    )}
                </p>
            )}

            {tab === 'preview' && !loading && !error && (
                <p className={`mb-4 text-sm font-semibold ${matched ? 'text-navy-700 dark:text-white' : 'text-gray-500'}`}>
                    {preview.length} lead{preview.length > 1 ? 's' : ''} refusé{preview.length > 1 ? 's' : ''}
                    {since ? ` depuis le ${formatDay(since)}` : ' sur les 14 derniers jours'} · {matched} compatible{matched > 1 ? 's' : ''} avec une offre
                    {matched === 0 && preview.length > 0 && (
                        <span className="font-normal"> — aucun ne correspond à une offre active (produit, département ou profil), rien à lancer.</span>
                    )}
                </p>
            )}

            {loading ? (
                <div className="py-10 text-center text-gray-400">Chargement...</div>
            ) : tab === 'preview' ? (
                preview.length === 0 ? (
                    <div className="py-10 text-center text-gray-400">Aucun lead refusé par Batiweb en attente.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-navy-600">
                                    <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Lead</th>
                                    <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Produit</th>
                                    <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Département</th>
                                    <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Refus Batiweb</th>
                                    <th className="pb-3 text-xs font-bold uppercase text-gray-500">Irait à</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preview.map((lead) => (
                                    <tr key={lead._id} className="border-b border-gray-100 align-top dark:border-navy-700">
                                        <td className="py-3 pr-4">
                                            <div className="font-semibold text-navy-700 dark:text-white">{fullName(lead.contact)}</div>
                                            <div className="text-xs text-gray-500">
                                                {lead.contact.email || '—'} · créé le {formatDate(lead.createdAt)}
                                            </div>
                                            <div className="font-mono text-xs text-gray-400">{lead._id}</div>
                                        </td>
                                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                                            {lead.subIndustry || '—'}
                                            <div className="text-xs text-gray-400">{lead.industry || ''}</div>
                                        </td>
                                        <td className="py-3 pr-4 text-xs text-gray-600 dark:text-gray-300">{lead.region || lead.contact.zipcode || '—'}</td>
                                        <td className="py-3 pr-4 text-xs text-gray-600 dark:text-gray-300">{lead.reason ? REASON_LABELS[lead.reason] : '—'}</td>
                                        <td className="py-3 text-xs">
                                            {lead.offerId ? (
                                                <span className="rounded-full bg-purple-100 px-2 py-0.5 font-semibold text-purple-700">{lead.offerName || lead.offerId}</span>
                                            ) : (
                                                <>
                                                    <span className="text-gray-400">Aucune offre — reste sur la Bourse</span>
                                                    {lead.mismatches.length === 0 ? (
                                                        <div className="mt-1 text-gray-400">Aucune offre locked active.</div>
                                                    ) : (
                                                        <ul className="mt-1 space-y-0.5 text-gray-500 dark:text-gray-400">
                                                            {lead.mismatches.map((m) => (
                                                                <li key={m.offerName}>
                                                                    <span className="font-semibold">{m.offerName}</span> : {m.reason}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ) : dispatched.length === 0 ? (
                <div className="py-10 text-center text-gray-400">Aucun lead attribué par le waterfall.</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Lead</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Produit</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Offre / acheteur</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Refus Batiweb</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Attribué le</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Prix</th>
                                <th className="pb-3 text-xs font-bold uppercase text-gray-500">Statut</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dispatched.map((lead) => (
                                <tr key={lead._id} className="border-b border-gray-100 align-top dark:border-navy-700">
                                    <td className="py-3 pr-4">
                                        <div className="font-semibold text-navy-700 dark:text-white">{fullName(lead.contact)}</div>
                                        <div className="text-xs text-gray-500">
                                            {lead.contact.email || '—'} · {lead.contact.zipcode || 'CP ?'}
                                        </div>
                                        <div className="font-mono text-xs text-gray-400">{lead._id}</div>
                                    </td>
                                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                                        {lead.subIndustry || '—'}
                                        <div className="text-xs text-gray-400">{lead.industry || ''}</div>
                                    </td>
                                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                                        {lead.offerName || '—'}
                                        <div className="text-xs text-gray-400">{lead.buyerEmail || '—'}</div>
                                    </td>
                                    <td className="py-3 pr-4 text-xs text-gray-600 dark:text-gray-300">{REASON_LABELS[lead.reason]}</td>
                                    <td className="py-3 pr-4 text-xs text-gray-500 whitespace-nowrap">
                                        {formatDate(lead.dispatchedAt)}
                                        <div className="text-gray-400">{lead.dispatchedBy === 'auto' ? 'automatique' : lead.dispatchedBy}</div>
                                    </td>
                                    <td className="py-3 pr-4 font-mono text-xs whitespace-nowrap text-gray-600 dark:text-gray-300">
                                        <div>A : {formatEuro(lead.buyerPrice)}</div>
                                        <div>V : {formatEuro(lead.leadPrice)}</div>
                                    </td>
                                    <td className="py-3 text-xs text-gray-600 dark:text-gray-300">
                                        {APPROVAL_LABELS[lead.approvalStatus || ''] || lead.approvalStatus || '—'}
                                        {lead.leadPrice > 0 && <div className={lead.paidAt ? 'text-gray-400' : 'text-orange-600'}>{lead.paidAt ? 'Vendeur payé' : 'Vendeur à payer'}</div>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
